// @flow

import React, { useState, useEffect } from "react";
import { localize } from "redux-i18n";
import classNames from "classnames";
import type { FormProps } from "redux-form";

import type { AbsenceReason } from "@modules/shared/reducer/absence-reasons/types";
import { Button, Icons, Input, ComboBox, View, Text } from "@components";
import type { OptionType } from "@components";
import type {
  EmailNotification,
  ReasonSpecificInstruction,
} from "@modules/service/reducer/email-notifications/types";

type OwnProps = $Exact<{
  data: ?EmailNotification,
  schema: Object,
  reasons: Array<AbsenceReason>,
  editing?: boolean,
  onChange?: (data: Object) => void,
  onSubmit?: (data: Object) => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
}>;

type ReasonAndInstructionText = {
  absenceReason: AbsenceReason,
  instructionText: string,
};

type OwnState = $Exact<{
  active: ?number,
  reasonSpecificInstructions: EmailNotification | Object,
}>;

export function EmailNotificationReasonSpecificInstructions(props: Props) {
  const [active, setActive] = useState(null);
  const [reasonSpecificInstructions, setReasonSpecificInstructions]: [
    ReasonAndInstructionText[],
    Function,
  ] = useState(props.data.reasonSpecificInstructions || []);

  const data = props.data;

  useEffect(() => {
    if (props.onChange) {
      props.onChange({ ...data, reasonSpecificInstructions: reasonSpecificInstructions });
    }
  }, [reasonSpecificInstructions]);

  const selectedReasonsIds = reasonSpecificInstructions.map(r => r.absenceReason.id);
  const inactiveReasonsIds = props.reasons
    .filter((reason: AbsenceReason) => !reason.isActive)
    .map((reason: AbsenceReason) => reason.id);

  const reasons = props.reasons
    .filter(r => r.isActive)
    .filter(r => !selectedReasonsIds.includes(r.id));

  const hasToSelectReasonFirst = !!reasonSpecificInstructions.filter(r => r.absenceReason.id == -1)
    .length;

  function handleUpdateReason(reasonInstructionIdx: number) {
    return function(reasonId: OptionType) {
      const newReason = reasons.find(r => reasonId == r.id);
      if (newReason) {
        const fd = [...reasonSpecificInstructions];
        fd[reasonInstructionIdx].absenceReason = newReason;
        setReasonSpecificInstructions(fd);
      }
    };
  }

  function handleUpdateInstruction(reasonInstructionIdx: number) {
    return function(e: SyntheticEvent<HTMLInputElement>) {
      if (e.currentTarget && e.currentTarget.value) {
        const val = e.currentTarget.value;
        const fd = [...reasonSpecificInstructions];
        fd[reasonInstructionIdx].instructionText = val;
        setReasonSpecificInstructions(fd);
      }
    };
  }

  function handleRemoveInstruction(reasonInstructionIdx: number) {
    return function() {
      setReasonSpecificInstructions(
        reasonSpecificInstructions.filter((_, index) => index !== reasonInstructionIdx),
      );
    };
  }

  function handleAddInstruction() {
    setReasonSpecificInstructions([
      ...reasonSpecificInstructions,
      {
        absenceReason: {
          id: -1,
          revisionId: null,
          label: "- Select reason -",
          typeId: null,
          createdAt: null,
          isActive: false,
          name: "",
        },
        instructionText: "",
      },
    ]);
  }

  function handleMouseEnter(id: number) {
    return () => {
      setActive(id);
    };
  }

  function handleMouseLeave() {
    setActive(null);
  }

  return (
    <div className="w-100 d-flex flex-column">
      {props.editing && (
        <View direction="row" className="d-flex justify-content-end">
          <Button
            materialIcon="add_box"
            className="link eap-rsu__add-button"
            onClick={handleAddInstruction}
            disabled={hasToSelectReasonFirst}
          >
            {props.t("ADD")}
          </Button>
        </View>
      )}
      <div className="record-table">
        <View direction="row" className="record-table__header">
          <View flex="3">{props.t("EMAIL_NOTIFICATION_ABSENCE_REASON")}</View>
          <View flex="8">{props.t("EMAIL_NOTIFICATION_INSTRUCTION_TEXT")}</View>
          <View flex="1" />
        </View>
        {reasonSpecificInstructions &&
          reasonSpecificInstructions.map((reasonInstruction, reasonInstructionIdx) => (
            <View
              direction="row"
              key={reasonInstructionIdx}
              className={classNames("record-table__row d-flex flex-direction-column", {
                "record-table__row--active": props.editing && active === reasonInstructionIdx,
              })}
              onMouseEnter={handleMouseEnter(reasonInstructionIdx)}
              onMouseLeave={handleMouseLeave}
            >
              <View flex="3" justifyContent="center" className="p-0 reason">
                {props.editing && active === reasonInstructionIdx ? (
                  <ComboBox
                    value={reasonInstruction.absenceReason}
                    className="combobox"
                    options={reasons}
                    onChange={handleUpdateReason(reasonInstructionIdx)}
                    withoutEmptyOption
                  />
                ) : (
                  <label>{reasonInstruction.absenceReason.label}</label>
                )}
                {!reasonInstruction.absenceReason.isActive && (
                  <Text color="negative">{props.t("INACTIVE_REASON_LABEL")}</Text>
                )}
              </View>
              <View flex="8" className="text">
                {props.editing && active === reasonInstructionIdx ? (
                  <Input
                    type="textarea"
                    rows={3}
                    name="instructionText"
                    value={reasonInstruction.instructionText}
                    onChange={handleUpdateInstruction(reasonInstructionIdx)}
                  />
                ) : (
                  <label>{reasonInstruction.instructionText}</label>
                )}
              </View>
              <View justifyContent="center" className="delete">
                {props.editing && (
                  <Icons.CloseIcon
                    className="pointer"
                    onClick={handleRemoveInstruction(reasonInstructionIdx)}
                  />
                )}
              </View>
            </View>
          ))}
      </div>
    </div>
  );
}

export default localize()(EmailNotificationReasonSpecificInstructions);
